import React, { useState, useEffect } from 'react';
import type { Person } from './types';
import { X, Calendar, MapPin, Users, FileText, Plus, Trash2 } from 'lucide-react';

interface PersonFormProps {
  person?: Person;
  people: Record<string, Person>;
  isOpen: boolean;
  onClose: () => void;
  onSave: (person: Person) => void;
}

const PersonForm: React.FC<PersonFormProps> = ({ 
  person, 
  people: _people, 
  isOpen, 
  onClose, 
  onSave 
}) => {
  // Note: _people prop will be used for relationship management in future iterations
  const [formData, setFormData] = useState<Partial<Person>>({
    name: '',
    gender: 'male',
    birthDate: '',
    birthPlace: '',
    deathDate: '',
    deathPlace: '',
    iwi: [],
    hapu: [],
    parents: [],
    spouses: [],
    children: [],
    notes: '',
    photos: [],
    stories: [],
    documents: []
  });

  const [newIwi, setNewIwi] = useState('');
  const [newHapu, setNewHapu] = useState('');
  const [newStory, setNewStory] = useState('');

  useEffect(() => {
    if (person) {
      setFormData(person);
    } else {
      setFormData({
        name: '',
        gender: 'male',
        birthDate: '',
        birthPlace: '',
        deathDate: '',
        deathPlace: '',
        iwi: [],
        hapu: [],
        parents: [],
        spouses: [],
        children: [],
        notes: '',
        photos: [],
        stories: [],
        documents: []
      });
    }
  }, [person, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;

    const newPerson: Person = {
      id: person?.id || `person-${Date.now()}`,
      name: formData.name,
      gender: formData.gender as 'male' | 'female' | 'other',
      birthDate: formData.birthDate,
      birthPlace: formData.birthPlace,
      deathDate: formData.deathDate,
      deathPlace: formData.deathPlace,
      iwi: formData.iwi || [],
      hapu: formData.hapu || [],
      parents: formData.parents || [],
      spouses: formData.spouses || [],
      children: formData.children || [],
      notes: formData.notes,
      photos: formData.photos || [],
      stories: formData.stories || [],
      documents: formData.documents || []
    };

    onSave(newPerson);
    onClose();
  };

  const handleAddIwi = () => {
    if (newIwi && !formData.iwi?.includes(newIwi)) {
      setFormData(prev => ({
        ...prev,
        iwi: [...(prev.iwi || []), newIwi]
      }));
      setNewIwi('');
    }
  };

  const handleAddHapu = () => {
    if (newHapu && !formData.hapu?.includes(newHapu)) {
      setFormData(prev => ({
        ...prev,
        hapu: [...(prev.hapu || []), newHapu]
      }));
      setNewHapu('');
    }
  };

  const handleAddStory = () => {
    if (newStory) {
      setFormData(prev => ({
        ...prev,
        stories: [...(prev.stories || []), newStory]
      }));
      setNewStory('');
    }
  };

  const removeItem = (array: string[], index: number, field: keyof Person) => {
    setFormData(prev => ({
      ...prev,
      [field]: array.filter((_, i) => i !== index)    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">
            {person ? 'Edit Person' : 'Add New Person'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
                placeholder="Enter full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender
              </label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value as 'male' | 'female' | 'other' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Birth Date
              </label>
              <input
                type="date"
                value={formData.birthDate}
                onChange={(e) => setFormData(prev => ({ ...prev, birthDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Death Date
              </label>
              <input
                type="date"
                value={formData.deathDate}
                onChange={(e) => setFormData(prev => ({ ...prev, deathDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
              />
            </div>
          </div>

          {/* Places */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="h-4 w-4 inline mr-1" />
                Birth Place
              </label>
              <input
                type="text"
                value={formData.birthPlace}
                onChange={(e) => setFormData(prev => ({ ...prev, birthPlace: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
                placeholder="City, Region"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="h-4 w-4 inline mr-1" />
                Death Place
              </label>
              <input
                type="text"
                value={formData.deathPlace}
                onChange={(e) => setFormData(prev => ({ ...prev, deathPlace: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
                placeholder="City, Region"
              />
            </div>
          </div>

          {/* Iwi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="h-4 w-4 inline mr-1" />
              Iwi
            </label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newIwi}
                onChange={(e) => setNewIwi(e.target.value)}
                placeholder="Add iwi affiliation"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddIwi())}
              />
              <button
                type="button"
                onClick={handleAddIwi}
                className="px-3 py-2 bg-ocean-teal text-white rounded-md hover:bg-ocean-deep"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.iwi?.map((iwi, index) => (
                <span
                  key={index}
                  className="bg-ocean-blue bg-opacity-20 text-ocean-deep px-3 py-1 rounded-full text-sm flex items-center"
                >
                  {iwi}
                  <button
                    type="button"
                    onClick={() => removeItem(formData.iwi!, index, 'iwi')}
                    className="ml-2 text-ocean-teal hover:text-ocean-deep"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Hapu */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="h-4 w-4 inline mr-1" />
              Hapū
            </label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newHapu}
                onChange={(e) => setNewHapu(e.target.value)}
                placeholder="Add hapū affiliation"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHapu())}
              />
              <button
                type="button"
                onClick={handleAddHapu}
                className="px-3 py-2 bg-ocean-teal text-white rounded-md hover:bg-ocean-deep"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.hapu?.map((hapu, index) => (
                <span
                  key={index}
                  className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm flex items-center"
                >
                  {hapu}
                  <button
                    type="button"
                    onClick={() => removeItem(formData.hapu!, index, 'hapu')}
                    className="ml-2 text-purple-600 hover:text-purple-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Stories & Memories */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="h-4 w-4 inline mr-1" />
              Stories & Memories
            </label>
            <div className="space-y-2 mb-2">
              <textarea
                value={newStory}
                onChange={(e) => setNewStory(e.target.value)}
                placeholder="Add a story or memory..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
              />
              <button
                type="button"
                onClick={handleAddStory}
                className="px-4 py-2 bg-ocean-teal text-white rounded-md hover:bg-ocean-deep flex items-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Story
              </button>
            </div>
            <div className="space-y-2">
              {formData.stories?.map((story, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-md relative">
                  <p className="text-sm text-gray-700 pr-8">{story}</p>
                  <button
                    type="button"
                    onClick={() => removeItem(formData.stories!, index, 'stories')}
                    className="absolute top-2 right-2 text-gray-400 hover:text-ocean-teal"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
              placeholder="Additional notes about this person..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-ocean-teal text-white rounded-md hover:bg-ocean-deep"
            >
              {person ? 'Update Person' : 'Add Person'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonForm;
